// examApiClient.js — THE security boundary for exam operations (ADR-1)
//
// Used ONLY during active exam on native app clients (Electron/Catalyst).
// All other operations use supabaseClient.js directly.
//
// Auth: Authorization: Bearer {sessionToken} on EVERY request
// Retry: 3x with exponential backoff on network errors
// Base URL: window.examApiBaseUrl (injected by Electron/Catalyst preload)

import { getDutchError } from '../constants/examConstants';
import * as examResume from './examResume';
import { supabase } from './supabase';
import { attestHeaders, kanOndertekenen } from '../utils/appAttest';

const MAX_RETRIES = 3;
const BASE_BACKOFF_MS = 2000;
const REQUEST_TIMEOUT_MS = 30000;

function getBaseUrl() {
  if (window.examApiBaseUrl) return window.examApiBaseUrl;
  // Strip trailing /api — exam paths already include /api/exam/...
  const raw = process.env.REACT_APP_BACKEND_URL || 'https://backend.testwisely.nl';
  return raw.replace(/\/api\/?$/, '');
}

let currentSessionToken = null;
// Eerste heartbeat-challenge van /start-anon (null na een herstel: de hook
// stuurt dan een reconnect en krijgt van de server een nieuwe challenge).
let initialChallenge = null;

export function getInitialChallenge() {
  return initialChallenge;
}

// Weigeringen om de app-handtekening (backend appStartBeleid.js, 23-10-2026).
// De server geeft de melding al in gewone taal; die tonen we zoals hij is.
// Uitzondering: de pagina draait wél in een app die kan ondertekenen (2.3+),
// maar de server zag geen geldige handtekening. Dan is het geen oude app maar
// een mislukte poging (brug te traag, app zonder geheim gebouwd).
const APP_HANDTEKENING_CODES = new Set(['APP_BIJWERKEN', 'APP_VEREIST', 'APP_KLOK']);
export function appHandtekeningMelding(data) {
  if (!data || !APP_HANDTEKENING_CODES.has(data.errorCode)) return null;
  if (data.errorCode === 'APP_BIJWERKEN' && kanOndertekenen()) {
    return 'De TestWisely-app kon de start niet ondertekenen. Probeer het nog een keer. Lukt het dan niet, werk de app bij via de App Store of vraag je docent om hulp.';
  }
  return data.error || 'Deze toets maak je in de bijgewerkte TestWisely-app. Werk de TestWisely-app bij via de App Store.';
}

/**
 * Make an authenticated request to the exam API with retry logic.
 */
async function examFetch(path, opties = {}) {
  // `onderteken`: vraag de iPad-app om een handtekening (sprint 006 A3). Per
  // poging opnieuw, want de server weigert een nonce die hij al zag.
  const { onderteken = false, ...options } = opties;
  const url = `${getBaseUrl()}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (currentSessionToken) {
    headers['Authorization'] = `Bearer ${currentSessionToken}`;
  }

  let lastError = null;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const pogingHeaders = onderteken
        ? { ...headers, ...(await attestHeaders(options.method || 'GET', url)) }
        : headers;
      const response = await fetch(url, {
        ...options,
        headers: pogingHeaders,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Don't retry on client errors (4xx) — only on server/network errors
      if (response.status >= 400 && response.status < 500) {
        const data = await response.json().catch(() => ({}));
        const appMelding = appHandtekeningMelding(data);
        const error = new Error(appMelding || data.error || 'Request failed');
        error.status = response.status;
        if (data.errorCode) error.errorCode = data.errorCode;
        error.dutchMessage = appMelding || getDutchError(data.error);
        throw error;
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;
      // Don't retry client errors
      if (err.status && err.status >= 400 && err.status < 500) {
        throw err;
      }
      // Exponential backoff for network/server errors (including timeout aborts)
      if (attempt < MAX_RETRIES - 1) {
        await new Promise(r => setTimeout(r, BASE_BACKOFF_MS * Math.pow(2, attempt)));
      }
    }
  }

  // All retries failed
  const error = new Error('Verbinding verloren. Controleer je internetverbinding.');
  error.isNetworkError = true;
  error.dutchMessage = 'Verbinding verloren. Controleer je internetverbinding.';
  error.originalError = lastError;
  throw error;
}

/**
 * Start an exam session. Returns { sessionId, sessionToken, challenge, resumed, tijdslimiet }.
 */
export async function startExam(toetsCode, leerlingId, schoolId, resumeToken = null) {
  const platform = window.isNativeApp ? (window.supportsAssessmentMode ? 'catalyst' : 'electron') : 'browser';
  const body = { toetsCode, leerlingId, schoolId, platform };
  // EXAM-01: to resume an already-active session the client must prove it owns
  // the token (crash recovery). Without it the backend returns 409 instead of
  // handing the live session token to any caller who knows the leerlingId.
  if (resumeToken) body.resumeToken = resumeToken;

  // Fase C punt 9: de backend eist nu het Supabase-JWT en leidt de leerling
  // daaruit af (leerlingId in de body is alleen nog ter controle/logging).
  currentSessionToken = null; // een nieuwe start draagt nooit een oud toets-token mee (dat zou het JWT overschrijven)
  let jwt = null;
  try { const { data: { session } } = await supabase.auth.getSession(); jwt = session?.access_token || null; } catch (_) { /* geen sessie */ }
  const result = await examFetch('/api/exam/start', {
    method: 'POST',
    onderteken: true,
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    body: JSON.stringify(body),
  });

  currentSessionToken = result.sessionToken;
  // Persist for same-tab refresh recovery (browser). Electron adds its own
  // encrypted crash-recovery store on top of this.
  try {
    if (result.sessionId && result.sessionToken) {
      sessionStorage.setItem(`tw-exam-token-${result.sessionId}`, result.sessionToken);
    }
  } catch (_) { /* storage unavailable — non-fatal */ }
  return result;
}

/**
 * Look up a previously stored exam token for a session (same-tab refresh recovery).
 */
export function getStoredResumeToken(sessionId) {
  try {
    return sessionStorage.getItem(`tw-exam-token-${sessionId}`) || null;
  } catch (_) {
    return null;
  }
}

/**
 * Send a heartbeat with challenge-response.
 * Returns { next_challenge, server_time }.
 */
export async function sendHeartbeat(sessionId, challengeResponse) {
  const platform = window.isNativeApp ? (window.supportsAssessmentMode ? 'catalyst' : 'electron') : 'browser';
  return examFetch(`/api/exam/${sessionId}/heartbeat`, {
    method: 'POST',
    body: JSON.stringify({ challenge_response: challengeResponse, platform }),
  });
}

/**
 * Get all exam questions (shuffled, without answers).
 * Returns { questions, existingAnswers }.
 */
export async function getQuestions(sessionId) {
  return examFetch(`/api/exam/${sessionId}/questions`);
}

/**
 * Submit a single answer.
 */
export async function submitAnswer(sessionId, questionId, answer) {
  return examFetch(`/api/exam/${sessionId}/answer`, {
    method: 'POST',
    body: JSON.stringify({ questionId, answer }),
  });
}

/**
 * Submit the entire exam.
 */
export async function submitExam(sessionId) {
  const result = await examFetch(`/api/exam/${sessionId}/submit`, {
    method: 'POST',
  });
  currentSessionToken = null;
  return result;
}

// ════════════════════════════════════════════════════════════
// Sessie-token-flow (fase 2 sessie-token-plan): pincode-modus in de browser
// én native draait op deze endpoints i.p.v. directe Supabase-anon-calls.
// ════════════════════════════════════════════════════════════

/**
 * Start een anonieme (pincode) sessie. Response bevat het sessie-token dat
 * alle vervolg-calls autoriseert. Fouten met errorCode (POGINGEN_LIMIET,
 * MAX_DEELNEMERS, EXAM_NOT_STARTED, EXAM_CLOSED) komen als err.status=403.
 */
export async function startAnonExam(toetsCode, leerlingNaam, resumeToken = null) {
  // Platform meesturen zoals /start dat doet: zonder dit stond elke pincode-sessie
  // als 'browser' in de monitor, ook op de iPad met een geldig app-attest.
  const platform = window.isNativeApp ? (window.supportsAssessmentMode ? 'catalyst' : 'electron') : 'browser';
  const body = { toetsCode, leerlingNaam, platform };
  // Crash-recovery: als de caller een eerder token heeft, meesturen zodat de
  // server een bestaande actieve sessie mag hervatten (anders 409 — de server
  // geeft een live token nooit aan wie enkel code+naam kent, B2).
  if (resumeToken) body.resumeToken = resumeToken;
  const result = await examFetch('/api/exam/start-anon', {
    method: 'POST',
    onderteken: true,
    body: JSON.stringify(body),
  });
  currentSessionToken = result.sessionToken;
  // Windows-audit 02-09: de server geeft nu een challenge terug, zodat ook de
  // pincode-flow heartbeats stuurt. ToetsMaken leest hem via getInitialChallenge().
  initialChallenge = result.challenge || null;
  try {
    if (result.sessionId && result.sessionToken) {
      sessionStorage.setItem(`tw-exam-token-${result.sessionId}`, result.sessionToken);
    }
  } catch (_) { /* storage unavailable — non-fatal */ }
  // Blok 5: ook in localStorage, zodat een leerling na een lege batterij of
  // dichtgeklapte laptop (browser afgesloten) terugkomt in dezelfde sessie.
  // Tot het einde van de toetstijd (Bijlage 1, A3): tijdslimiet, plus 25% bij
  // voorlezen (zelfde regel als ToetsMaken zonder percentage van de leerling).
  // Zonder tijdslimiet bepaalt examResume de termijn (4 uur, zoals de server).
  const minuten = Number(result.tijdslimiet);
  const verlooptOp = minuten > 0
    ? Date.now() + Math.ceil(minuten * 60 * 1000 * (result.ttsEnabled ? 1.25 : 1))
    : undefined;
  examResume.bewaarSessie({ sessionId: result.sessionId, sessionToken: result.sessionToken, toetsCode, leerlingNaam, verlooptOp });
  return result;
}

/**
 * Herstel het token voor een bestaande sessie (page-refresh / navigatie vanaf
 * de startpagina / na submit voor nakijken+resultaat). Retourneert true als
 * er een token is — dat is de proxyMode-switch in ToetsMaken.
 */
export function restoreSession(sessionId) {
  const token = getStoredResumeToken(sessionId) || examResume.tokenBijSessie(sessionId);
  if (token) {
    currentSessionToken = token;
    return true;
  }
  return false;
}

/**
 * Sessie + antwoorden + resultaten (ook na submit, binnen de grace).
 * ruimte=leerling (16-10-2026): de afname krijgt volgorde-items en de
 * rechterkolom van koppelvragen gesorteerd (backend/src/exam/leerlingVraag.js);
 * opgeslagen antwoorden komen dan in diezelfde ruimte terug.
 */
export async function getState(sessionId) {
  return examFetch(`/api/exam/${sessionId}/state?ruimte=leerling`);
}

/** De toets van deze sessie incl. vragen (voor ToetsMaken + wachtruimte-poll). */
export async function getTest(sessionId) {
  return examFetch(`/api/exam/${sessionId}/test`);
}

/** Blok 5: alleen of de toets actief is (wachtruimte-poll). Eén lichte query,
 *  geen vragen, i.p.v. de hele toets elke 5 s. */
export async function getStatus(sessionId) {
  return examFetch(`/api/exam/${sessionId}/status`);
}

/**
 * Antwoord-rijen opslaan (upsert_antwoorden-formaat; sessie_id dwingt de server af).
 * antwoordRuimte 'leerling': de indexen horen bij de vragen zoals /test ze gaf;
 * de server vertaalt ze terug naar de database (zie getState).
 */
export async function saveAnswerRows(sessionId, rows) {
  return examFetch(`/api/exam/${sessionId}/answers`, {
    method: 'POST',
    body: JSON.stringify({ rows, antwoordRuimte: 'leerling' }),
  });
}

/**
 * Ingelogde leerling opent een eigen, lopende toets in een nieuw tabblad of op
 * een ander apparaat (16-10-2026). Het sessie-token stond alleen in het eerste
 * tabblad; zonder token las de afname de vragen rechtstreeks uit de database,
 * met de juiste antwoorden. De server geeft de eigenaar (Supabase-JWT) zijn
 * token terug; daarna loopt alles via de proxy, net als na een gewone start.
 * Gooit bij een fout (bijv. 409 als de toets al is ingeleverd).
 */
export async function hervatAccountSessie(sessionId) {
  const platform = window.isNativeApp ? (window.supportsAssessmentMode ? 'catalyst' : 'electron') : 'browser';
  currentSessionToken = null; // het JWT moet de Authorization-header zijn
  let jwt = null;
  try { const { data: { session } } = await supabase.auth.getSession(); jwt = session?.access_token || null; } catch (_) { /* geen sessie */ }
  if (!jwt) {
    const err = new Error('Niet ingelogd');
    err.status = 401;
    throw err;
  }
  const result = await examFetch(`/api/exam/${sessionId}/hervat`, {
    method: 'POST',
    onderteken: true,
    headers: { Authorization: `Bearer ${jwt}` },
    body: JSON.stringify({ platform }),
  });
  currentSessionToken = result.sessionToken;
  initialChallenge = result.challenge || null;
  try {
    if (result.sessionId && result.sessionToken) {
      sessionStorage.setItem(`tw-exam-token-${result.sessionId}`, result.sessionToken);
    }
  } catch (_) { /* storage unavailable — non-fatal */ }
  return result;
}

/** Voortgang (resterende tijd / vraagindex) opslaan. */
export async function saveProgress(sessionId, { timeRemaining, currentQuestionIndex } = {}) {
  const body = {};
  if (Number.isInteger(timeRemaining)) body.resterende_tijd_sec = timeRemaining;
  if (Number.isInteger(currentQuestionIndex)) body.huidige_vraag_index = currentQuestionIndex;
  return examFetch(`/api/exam/${sessionId}/progress`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Tijd om → sessie laten verlopen. */
export async function expireExam(sessionId) {
  return examFetch(`/api/exam/${sessionId}/expire`, { method: 'POST' });
}

/**
 * AI-nakijken van één open vraag ná inleveren. De server leest het
 * leerling-antwoord en het antwoordmodel zelf uit de DB.
 */
export async function gradeOpenViaSession(sessionId, questionId) {
  return examFetch(`/api/exam/${sessionId}/grade-open`, {
    method: 'POST',
    body: JSON.stringify({ questionId }),
  });
}

/**
 * Finaliseer: server clamp't scores, berekent totaal en schrijft resultaten.
 * grades: [{ questionId, points, feedback, aiGraded, aiConfidence }]
 */
export async function finalizeExam(sessionId, grades) {
  return examFetch(`/api/exam/${sessionId}/finalize`, {
    method: 'POST',
    body: JSON.stringify({ grades }),
  });
}

/**
 * Get the current session token (for useHeartbeat hook).
 */
export function getSessionToken() {
  return currentSessionToken;
}

/**
 * Clear the session token (on exam end or error).
 */
export function clearSession() {
  currentSessionToken = null;
  initialChallenge = null;
}

/**
 * Blok 5: probeer een eerder gestarte sessie terug te vinden voor (code, naam)
 * op DIT toestel. Retourneert { sessionId, sessionToken } of null. Wordt bij
 * het opnieuw inloggen gebruikt om de leerling terug te brengen waar hij was.
 */
export function hervatBijCode(toetsCode, leerlingNaam) {
  return examResume.sessieBijCode(toetsCode, leerlingNaam);
}

/** Wis de lokale herstelgegevens van een sessie (na inleveren). */
export function wisHerstel(sessionId, toetsCode, leerlingNaam) {
  examResume.wis(sessionId, toetsCode, leerlingNaam);
}

/**
 * Check if we're in exam mode (have an active session token).
 */
export function isExamMode() {
  return currentSessionToken !== null;
}
